import { parseFloatSafe } from '@amazon/vinyl-util'

export interface Rgb {
    r: number // 0–255
    g: number // 0–255
    b: number // 0–255
    a: number // 0-1
}

export interface Hsl {
    h: number // 0–360
    s: number // 0–1
    l: number // 0–1
    a: number // 0–1
}

/**
 * Convert Rgb → Hsl
 *
 * @param rgb The input Rgb to convert.
 * @param out Optionally provide the output Hsl to mutate. Creates a new object otherwise.
 */
export function rgbToHsl({ r, g, b, a }: Rgb, out?: Hsl): Hsl {
    const rNorm = r / 255
    const gNorm = g / 255
    const bNorm = b / 255

    const max = Math.max(rNorm, gNorm, bNorm)
    const min = Math.min(rNorm, gNorm, bNorm)
    const d = max - min

    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

        switch (max) {
            case rNorm:
                h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)
                break
            case gNorm:
                h = (bNorm - rNorm) / d + 2
                break
            case bNorm:
                h = (rNorm - gNorm) / d + 4
                break
        }
        h *= 60
    }

    const result = out || ({} as Hsl)
    result.h = h
    result.s = s
    result.l = l
    result.a = a
    return result
}

/**
 * Convert Hsl → Rgba
 *
 * @param hsl The input Hsl to convert.
 * @param out Optionally provide the output Rgb to mutate. Creates a new object otherwise.
 */
export function hslToRgb({ h, s, l, a }: Hsl, out?: Rgb): Rgb {
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1 / 6) return p + (q - p) * 6 * t
        if (t < 1 / 2) return q
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
        return p
    }

    let r: number, g: number, b: number

    if (s === 0) {
        r = g = b = l // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        const hk = h / 360
        r = hue2rgb(p, q, hk + 1 / 3)
        g = hue2rgb(p, q, hk)
        b = hue2rgb(p, q, hk - 1 / 3)
    }

    const result = out || ({} as Rgb)
    result.r = Math.round(r * 255)
    result.g = Math.round(g * 255)
    result.b = Math.round(b * 255)
    result.a = a
    return result
}

/**
 * Parses a string representation of a color.
 *
 * Supports #rrggbb, #rrggbbaa, #rgb, #rgba, hsl(h, s, l), hsla(h, s, l, a)
 *
 * @param input
 */
export function parseColor(input: string): Rgb | null {
    const color = input.trim().toLowerCase()

    // #rgb, #rgba, #rrggbb, #rrggbbaa
    if (color.startsWith('#')) {
        const hex = color.slice(1)
        if (hex.length === 3) {
            const [r, g, b] = hex.split('').map((x) => parseInt(x + x, 16))
            return { r, g, b, a: 1 }
        } else if (hex.length === 4) {
            const [r, g, b, a] = hex.split('').map((x) => parseInt(x + x, 16))
            return { r, g, b, a: a / 255 }
        } else if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16)
            const g = parseInt(hex.slice(2, 4), 16)
            const b = parseInt(hex.slice(4, 6), 16)
            return { r, g, b, a: 1 }
        } else if (hex.length === 8) {
            const r = parseInt(hex.slice(0, 2), 16)
            const g = parseInt(hex.slice(2, 4), 16)
            const b = parseInt(hex.slice(4, 6), 16)
            const a = parseInt(hex.slice(6, 8), 16) / 255
            return { r, g, b, a }
        }
    }

    // rgb() or rgba()
    const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/)
    if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map((p) => p.trim())
        const r = parseInt(parts[0], 10)
        const g = parseInt(parts[1], 10)
        const b = parseInt(parts[2], 10)
        const a = parseFloatSafe(parts[3]) ?? 1
        return { r, g, b, a }
    }

    // hsl() or hsla()
    const hslMatch = color.match(/^hsla?\(([^)]+)\)$/)
    if (hslMatch) {
        const parts = hslMatch[1].split(',').map((p) => p.trim())
        const h = parseFloat(parts[0])
        const s = parseFloat(parts[1]) / 100
        const l = parseFloat(parts[2]) / 100
        const a = parseFloatSafe(parts[3]) ?? 1

        return hslToRgb({ h, s, l, a })
    }

    return null
}

/**
 * Convert RGBA object → CSS rgba() string
 */
export function rgbaToCssString({ r, g, b, a }: Rgb): string {
    const rr = Math.round(r)
    const gg = Math.round(g)
    const bb = Math.round(b)
    const aa = +a.toFixed(3) // keep alpha to 3 decimals
    return `rgba(${rr}, ${gg}, ${bb}, ${aa})`
}

/**
 * Convert RGBA object → hex string (#rrggbb or #rrggbbaa).
 */
export function rgbToHex({ r, g, b, a }: Rgb): string {
    const toHex = (n: number) => n.toString(16).padStart(2, '0')

    const rr = toHex(Math.round(r))
    const gg = toHex(Math.round(g))
    const bb = toHex(Math.round(b))

    if (a >= 1) {
        return `#${rr}${gg}${bb}`
    } else {
        const aa = toHex(Math.round(a * 255))
        return `#${rr}${gg}${bb}${aa}`
    }
}

export function rgbToCss({ r, g, b }: Rgb): string {
    return `rgb(${r}, ${g}, ${b})`
}

export function hslToCss({ h, s, l }: Hsl): string {
    return `hsl(${Math.floor(h)}, ${Math.floor(s * 100)}%, ${Math.floor(l * 100)}%)`
}

/**
 * Scales an RGB color toward black by multiplying each channel by brightness.
 *
 * @param rgb - The color to modify, with channels in 0-1
 * @param brightness - Desired brightness (0 = black, 1 = original color)
 * @returns The darkened modified RGB color
 */
export function applyBrightnessRgb(rgb: Rgb, brightness: number): Rgb {
    rgb.r *= brightness
    rgb.g *= brightness
    rgb.b *= brightness
    return rgb
}
